import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  CountrySelect,
  InfoBar,
  Link,
  StateSelect,
  TextField,
  type TextFieldProps,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type { InputHTMLAttributes } from "react";
import { useWatch } from "react-hook-form";

import { checkoutBillingErrorContactUsURL } from "../../../../config";
import { formDataToBillingDetails } from "../../../../hooks/useCheckout";
import { useTracker } from "../../../../hooks/useTracker";
import { ElementClickAction } from "../../../../lib/TrackingAdapter";
import { ServiceErrorCode } from "../../../../types";
import { isCountryInEU } from "../../../../utils/countryUtils";
import {
  type CheckoutFormData,
  COUNTRIES_WITH_MANDATORY_STATE_SELECTOR,
  MANDATORY_BILLING_DATA_COUNTRIES,
} from "../../ChargebeeCheckoutSchema";
import { useCheckoutFormContext } from "../CheckoutFormProvider";

import style from "./BillingInfoForm.module.css";

const BillingDetailsStateSelect = ({
  name,
  ...args
}: InputHTMLAttributes<HTMLSelectElement> & TextFieldProps & { name: keyof CheckoutFormData }) => {
  const {
    methods: {
      register,
      formState: { errors },
      control,
      setValue,
    },
  } = useCheckoutFormContext();

  const countryCode = useWatch({ name: "countryCode", control }) ?? "";
  useEffect(() => {
    setValue(name, "");
  }, [countryCode, name, setValue]);

  if (!COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(countryCode)) {
    return null;
  }

  return (
    <StateSelect
      {...args}
      //eslint-disable-next-line
      countryCode={countryCode as any}
      defaultValue=""
      required
      errorMessage={errors[name]?.message}
      {...register(name)}
    />
  );
};

const BillingDetailsTextField = ({
  name,
  ...args
}: InputHTMLAttributes<HTMLInputElement> & TextFieldProps & { name: keyof CheckoutFormData }) => {
  const {
    methods: {
      register,
      formState: { errors },
    },
  } = useCheckoutFormContext();

  return (
    <TextField
      {...args}
      {...register(name)}
      autoComplete="off"
      autoSave="off"
      autoCapitalize="off"
      autoCorrect="off"
      aria-autocomplete="none"
      errorMessage={errors[name]?.message}
    />
  );
};

export default function BillingInfoForm() {
  const {
    methods: {
      register,
      setValue,
      control,
      formState: { errors, isLoading },
    },
    estimateMutationResult,
  } = useCheckoutFormContext();

  const tracker = useTracker();

  const { data: estimateData } = estimateMutationResult ?? {};
  const isBillingError = estimateData?.errors?.some(
    (error) => error.code === ServiceErrorCode.ValidationError
  );

  const [isBillingOpen, setIsBillingOpen] = useState(false);
  const countryCode = useWatch({ control, name: "countryCode" });
  const isBillingRequired = MANDATORY_BILLING_DATA_COUNTRIES.includes(countryCode || "");
  const formData = useWatch({ control }) as CheckoutFormData;
  const billingDetails = useMemo(() => formDataToBillingDetails(formData), [formData]);
  const hasBillingInfo = Object.entries(billingDetails ?? {}).some(
    ([key, value]) => key != "countryCode" && key != "migrationConsent" && value
  );
  useEffect(() => {
    if (!isLoading && (isBillingRequired || hasBillingInfo)) {
      setIsBillingOpen(true);
    }
  }, [isBillingRequired, hasBillingInfo, setValue, isLoading]);

  const onClickAddCompanyDetails = useCallback(() => {
    setIsBillingOpen(true);
    tracker.trackButtonClick({
      elementId: "add_billing_details",
      action: ElementClickAction.ADD_BILLING_DETAILS,
      details: "chargebee",
    });
  }, [tracker]);

  return (
    <>
      <div className={style.subgroup}>
        <div className={style.checkoutLineText}>
          Billing information<span>{isBillingRequired ? "" : " (Optional)"}</span>
        </div>
        <div>This information will be displayed on your invoice and used to calculate taxes.</div>
      </div>
      <div className={style.subgroup}>
        {!isBillingOpen && (
          <Button variant="grayscale" onClick={onClickAddCompanyDetails}>
            Add company details
          </Button>
        )}
        {isBillingOpen && isBillingError && (
          <InfoBar
            variant="critical"
            title="We couldn't verify your billing details."
            text={
              <>
                <span>
                  Please review your details. Let us know if we can help if the issue persists.
                </span>
                <br />
                <Link href={checkoutBillingErrorContactUsURL} target="_blank">
                  Contact Us
                </Link>
              </>
            }
          />
        )}
        <div className={classNames(style.companyDetailsForm, !isBillingOpen && style.hide)}>
          <BillingDetailsTextField name="company" placeholder="Name on the bill" maxLength={250} />
          <BillingDetailsTextField name="line1" placeholder="Address Line 1" maxLength={150} />
          <BillingDetailsTextField
            name="line2"
            placeholder={"Address Line 2" + (!isBillingRequired ? "" : " (Optional)")}
            maxLength={150}
          />
          <BillingDetailsTextField name="city" placeholder="City" maxLength={50} />
          <div className={style.creditCardLocationContainer}>
            <CountrySelect
              required
              errorMessage={errors.countryCode?.message}
              aria-label="Country"
              {...register("countryCode")}
            />
            <BillingDetailsTextField name="zip" placeholder="Post code" maxLength={20} />
          </div>
          <BillingDetailsStateSelect name="stateCode" />
          <BillingDetailsTextField
            name="vatNumber"
            maxLength={20}
            placeholder={
              isCountryInEU(countryCode)
                ? "VAT number (including country code)"
                : "Tax ID (Optional)"
            }
          />
        </div>
      </div>
    </>
  );
}
